import React, { useEffect, useMemo, useRef, useState } from "react";
import IncidentMap from "@/components/cad/IncidentMap";
import IncidentDetailDialog from "@/components/cad/IncidentDetailDialog";
import IncidentInfoDialog from "@/components/cad/IncidentInfoDialog";
import InlineCallEntry from "@/components/cad/InlineCallEntry";
import { useIncidents } from "@/contexts/IncidentsContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Crosshair, Pause, Play } from "lucide-react";
import { priorityColor } from "@/lib/api";
import { isIncidentVisible } from "@/lib/incidentVisibility";
import { getAccurateLocation } from "@/lib/geolocate";

const BRANCH_COLOR = { FIRE: "#EE352E", EMS: "#00B894", PD: "#0984E3", WEATHER: "#FDCB6E" };

export default function CadDashboard() {
  const { incidents, lastUpdated } = useIncidents();
  const [center, setCenter] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  // Read-only "incident detail" popup opened from the map's Show More button.
  const [infoOpen, setInfoOpen] = useState(false);
  const [infoIncident, setInfoIncident] = useState(null);
  const [autoFocus, setAutoFocus] = useState(true);
  const lastSeenRef = useRef(null);

  useEffect(() => {
    getAccurateLocation({
      onSuccess: ({ lat, lon }) => setCenter([lat, lon]),
      // On error keep center=null so IncidentMap falls back to its own default;
      // don't silently jump to the center of the USA.
    });
  }, []);

  // Auto-focus: when a new/updated incident arrives via WebSocket, fly to it
  useEffect(() => {
    if (!lastUpdated || !autoFocus) return;
    if (lastSeenRef.current === lastUpdated.id + ":" + lastUpdated.updated_at) return;
    lastSeenRef.current = lastUpdated.id + ":" + lastUpdated.updated_at;
    if (lastUpdated.lat && lastUpdated.lon) {
      setCenter([lastUpdated.lat, lastUpdated.lon]);
      setSelected(lastUpdated);
    }
  }, [lastUpdated, autoFocus]);

  // The map only shows incidents whose LAST activity (create / update / page /
  // update-log) is within the last 12 hours — matches the Active Incidents rule.
  // We recompute every 60 s so pins fall off cleanly as time crosses the cutoff.
  const [tick, setTick] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setTick(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);
  const visibleIncidents = useMemo(
    () => incidents.filter((i) => isIncidentVisible(i, tick)),
    [incidents, tick]
  );

  const openIncident = (inc) => {
    setSelected(inc);
    if (inc.lat && inc.lon) setCenter([inc.lat, inc.lon]);
  };

  const onCreated = (inc) => {
    if (inc.lat && inc.lon) setCenter([inc.lat, inc.lon]);
    setSelected(inc);
  };

  // Pin-drop reverse geocoding: dispatcher clicks anywhere on the map to auto-fill
  // the address form. The prefill payload is passed to InlineCallEntry which
  // applies it via useEffect.
  const [pinPrefill, setPinPrefill] = useState(null);
  const onMapClick = async (lat, lon) => {
    try {
      const { data } = await api.get("/geocode/reverse", { params: { lat, lon } });
      if (!data || !data.found) {
        toast.error("No address found at that point");
        return;
      }
      setCenter([lat, lon]);
      setPinPrefill({
        address: data.address_street || "",
        city: data.city || "",
        state: data.state || "",
        zip_code: data.zip_code || "",
        lat: String(lat),
        lon: String(lon),
        _stamp: Date.now(),
      });
      toast.success(`Address filled: ${data.address_street || data.display_name || "location"}`);
    } catch {
      toast.error("Reverse geocode failed");
    }
  };

  return (
    <div className="absolute inset-0 flex flex-col" data-testid="cad-dashboard">
      {/* Emergency banner removed — audio + map auto-focus + list flash convey new pages */}

      <div className="flex-1 flex flex-col md:flex-row overflow-hidden min-h-0">
        <div className="w-full md:w-96 border-b md:border-b-0 md:border-r border-border bg-card/40 flex flex-col shrink-0 max-h-[45vh] md:max-h-none">
          <InlineCallEntry onCreated={onCreated} mapCenter={center} prefill={pinPrefill} />
        </div>

        <div className="flex-1 relative min-w-0 min-h-[50vh] md:min-h-0">
          <IncidentMap
            incidents={visibleIncidents}
            center={center}
            onSelect={openIncident}
            onOpenDetail={(inc) => { setInfoIncident(inc); setInfoOpen(true); }}
            onOpenUpdate={(inc) => { setSelected(inc); setDetailOpen(true); }}
            onMapClick={onMapClick}
            selectedId={selected?.id}
            flyZoom={15}
            height="100%"
          />

          <div className="absolute top-3 left-3 z-[400] flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                getAccurateLocation({ onSuccess: ({ lat, lon }) => setCenter([lat, lon]) });
              }}
              data-testid="recenter-btn"
              className="shadow-lg"
            >
              <Crosshair className="w-4 h-4 mr-1" /> Re-Center
            </Button>
            <Button
              size="sm"
              variant={autoFocus ? "default" : "outline"}
              onClick={() => setAutoFocus((v) => !v)}
              data-testid="auto-focus-toggle"
              className="shadow-lg font-mono tracking-widest"
              title={autoFocus ? "Pause auto-focus on new/updated jobs" : "Resume auto-focus"}
            >
              {autoFocus ? <Pause className="w-3.5 h-3.5 mr-1" /> : <Play className="w-3.5 h-3.5 mr-1" />}
              {autoFocus ? "AUTO-FOCUS: ON" : "AUTO-FOCUS: OFF"}
            </Button>
          </div>

          <div className="absolute bottom-3 right-3 z-[400] bg-card/85 backdrop-blur border border-border rounded-sm px-3 py-2 text-xs font-mono shadow-lg">
            <span className="text-muted-foreground">PLOTTED</span>{" "}
            <span className="text-primary">{visibleIncidents.filter((i) => i.lat && i.lon).length}</span>
            <span className="text-muted-foreground"> / </span>
            <span>{visibleIncidents.length}</span>
          </div>
        </div>
      </div>

      <IncidentDetailDialog incident={selected} open={detailOpen} onOpenChange={setDetailOpen} />
      <IncidentInfoDialog
        incident={infoIncident}
        open={infoOpen}
        onOpenChange={setInfoOpen}
        onUpdate={(inc) => { setSelected(inc); setDetailOpen(true); }}
      />
    </div>
  );
}
